import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ChevronDown, ChevronUp, Shield, BarChart2 } from 'lucide-react';

const CONSENT_KEY = 'crmautomax_cookie_consent';
const CONSENT_VERSION = '1.0';

const defaultPrefs = {
  necessary: true,
  functional: false,
  analytics: false,
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const savedDate = new Date(parsed.savedAt);
        const now = new Date();
        const monthsAgo = (now - savedDate) / (1000 * 60 * 60 * 24 * 30);
        if (parsed.version === CONSENT_VERSION && monthsAgo < 12) return;
      }
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    } catch {
      setVisible(true);
    }
  }, []);

  const saveConsent = (preferences) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      preferences,
      savedAt: new Date().toISOString(),
    }));
    setVisible(false);
    setShowModal(false);
  };

  const acceptAll = () => {
    const all = { necessary: true, functional: true, analytics: true };
    setPrefs(all);
    saveConsent(all);
  };

  const rejectAll = () => {
    const minimal = { necessary: true, functional: false, analytics: false };
    setPrefs(minimal);
    saveConsent(minimal);
  };

  const savePreferences = () => saveConsent(prefs);

  const toggle = (key) => {
    if (key === 'necessary') return;
    setPrefs(p => ({ ...p, [key]: !p[key] }));
  };

  const toggleExpand = (key) => setExpanded(prev => (prev === key ? null : key));

  const categories = [
    {
      key: 'necessary',
      icon: <Shield size={18} color="#0891b2" />,
      label: 'Strictly Necessary',
      desc: 'Required for the platform to function. Includes authentication tokens and security cookies. Cannot be disabled.',
      required: true,
    },
    {
      key: 'functional',
      icon: <Cookie size={18} color="#7c3aed" />,
      label: 'Functional',
      desc: 'Remember your preferences such as dashboard layout and filter settings to enhance your experience.',
      required: false,
    },
    {
      key: 'analytics',
      icon: <BarChart2 size={18} color="#059669" />,
      label: 'Analytics',
      desc: 'Help us understand how the platform is used so we can improve it. All data is anonymised and aggregated.',
      required: false,
    },
  ];

  if (!visible && !showModal) return null;

  return (
    <>
      <style>{`
        @keyframes slideUp { from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);} }
        @keyframes fadeIn { from{opacity:0;}to{opacity:1;} }
        .cookie-accept-btn:hover { background: #2563eb !important; transform:translateY(-1px); }
        .cookie-reject-btn:hover { background:#f1f5f9 !important; border-color: #cbd5e1 !important; }
        .cookie-manage-btn:hover { color:#1e293b !important; }
        .toggle-track { transition: background 0.2s ease; }
        .cookie-cat-row:hover { background:#f8fafc !important; }
      `}</style>

      {/* ── Cookie Banner ── */}
      {visible && !showModal && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          width: 'calc(100% - 48px)', maxWidth: '800px', zIndex: 9999,
          background: '#ffffff',
          border: '1px solid #e2e8f0', borderRadius: '20px',
          padding: '20px 28px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          animation: 'slideUp 0.5s ease-out',
          display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap',
        }}>
          {/* Icon */}
          <div style={{
            width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
            background: '#eff6ff',
            border: '1px solid #dbeafe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Cookie size={24} color="#3b82f6" />
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
              Cookie Preferences
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6 }}>
              We use cookies to personalize content and analyze our traffic.{' '}
              <Link to="/cookie-policy" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>
                Read our Cookie Policy
              </Link>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <button
              className="cookie-manage-btn"
              onClick={() => setShowModal(true)}
              style={{
                background: 'none', border: 'none', color: '#64748b',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '8px 12px',
                transition: 'color 0.2s ease',
              }}
            >
              Settings
            </button>
            <button
              className="cookie-reject-btn"
              onClick={rejectAll}
              style={{
                background: '#ffffff', border: '1px solid #e2e8f0',
                color: '#475569', padding: '10px 20px', borderRadius: '12px',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease',
              }}
            >
              Essential Only
            </button>
            <button
              className="cookie-accept-btn"
              onClick={acceptAll}
              style={{
                background: '#3b82f6',
                border: 'none', color: 'white',
                padding: '10px 24px', borderRadius: '12px',
                fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s ease',
              }}
            >
              Accept All
            </button>
          </div>
        </div>
      )}

      {/* ── Preferences Modal ── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px', animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            width: '100%', maxWidth: '540px',
            background: '#ffffff',
            border: '1px solid #e2e8f0', borderRadius: '24px',
            overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '28px 32px 24px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: '#eff6ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Cookie size={22} color="#3b82f6" />
                </div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>Manage Cookies</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>Customize your privacy settings</div>
                </div>
              </div>
              <button
                onClick={() => { setShowModal(false); setVisible(true); }}
                style={{
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  borderRadius: '10px', width: '36px', height: '36px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#94a3b8', transition: 'all 0.2s ease',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Categories */}
            <div style={{ padding: '8px 0', maxHeight: '400px', overflowY: 'auto' }}>
              {categories.map((cat) => (
                <div key={cat.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <div
                    className="cookie-cat-row"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '16px',
                      padding: '20px 32px', cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => toggleExpand(cat.key)}
                  >
                    {/* Category Icon */}
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                      background: '#f8fafc', border: '1px solid #f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {cat.icon}
                    </div>

                    {/* Label */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{cat.label}</span>
                        {cat.required && (
                          <span style={{
                            fontSize: '11px', fontWeight: '700', color: '#0891b2',
                            background: '#ecfeff', border: '1px solid #cffafe',
                            padding: '2px 8px', borderRadius: '100px',
                          }}>Required</span>
                        )}
                      </div>
                    </div>

                    {/* Toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        className="toggle-track"
                        onClick={(e) => { e.stopPropagation(); toggle(cat.key); }}
                        style={{
                          width: '44px', height: '24px', borderRadius: '12px',
                          background: prefs[cat.key] ? '#3b82f6' : '#e2e8f0',
                          position: 'relative', cursor: cat.required ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: '3px',
                          left: prefs[cat.key] ? '23px' : '3px',
                          width: '18px', height: '18px', borderRadius: '50%',
                          background: 'white', transition: 'left 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }} />
                      </div>
                      {expanded === cat.key
                        ? <ChevronUp size={16} color="#94a3b8" />
                        : <ChevronDown size={16} color="#94a3b8" />}
                    </div>
                  </div>

                  {/* Expanded description */}
                  {expanded === cat.key && (
                    <div style={{ padding: '0 32px 20px 88px' }}>
                      <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                        {cat.desc}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '24px 32px',
              background: '#f8fafc',
              borderTop: '1px solid #f1f5f9',
              display: 'flex', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap',
            }}>
              <button
                onClick={rejectAll}
                style={{
                  background: '#ffffff', border: '1px solid #e2e8f0',
                  color: '#64748b', padding: '12px 20px', borderRadius: '12px',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease',
                }}
              >
                Reject All
              </button>
              <button
                onClick={savePreferences}
                style={{
                  background: '#3b82f6',
                  border: 'none', color: 'white',
                  padding: '12px 28px', borderRadius: '12px',
                  fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s ease',
                }}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
